/**
 * Loads contact-scoped link corpus once per open chat conversation (non-blocking).
 * See docs/ADMIN_CHAT_ENTITY_LINKS.md
 */
import { useEffect, useState } from 'react';
import BackendService from '../../services/admin/BackendService';
import { buildChatLinkCorpus, ChatLinkTarget } from '../../utils/chatLinkCorpus';
import { debugChatLinks } from '../../utils/chatLinkDebug';

export default function useChatLinkCorpus(
  contactExternalId: string | null,
  conversationId: string | null,
) {
  const [corpus, setCorpus] = useState<ChatLinkTarget[] | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!contactExternalId || !conversationId) {
      setCorpus(null);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setCorpus(null);

    Promise.all([
      new BackendService().getContactInvestments(contactExternalId),
      new BackendService().getInvestmentEntitiesForUser(contactExternalId),
    ])
      .then(([investments, entities]) => {
        if (cancelled) return;
        debugChatLinks('fetch complete', {
          contactExternalId,
          conversationId,
          investmentCount: investments?.length ?? 0,
          entityCount: entities?.length ?? 0,
        });
        setCorpus(buildChatLinkCorpus(investments ?? [], entities ?? [], conversationId));
      })
      .catch((error) => {
        console.error('Failed to load chat link corpus', error);
        debugChatLinks('fetch failed', { contactExternalId, conversationId, error });
        if (!cancelled) {
          setCorpus([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [contactExternalId, conversationId]);

  return { corpus, loading };
}
